/**
 * Real 404 page — replaces the silent redirect to home (CUSTOMER_FEEDBACK_PLAN Task 3.2).
 *
 * One-click "Report this broken link" opens ReportProblemDialog pre-filled as a
 * bug with surfaceId page.404 and the failing route in the allowlisted payload.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReportProblemDialog } from "../components/feedback/ReportProblemDialog.js";
import { Button } from "../components/ui/Button.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { redactRoute } from "../lib/feedback/context.js";

export function NotFoundPage() {
  const location = useLocation();
  const [reportOpen, setReportOpen] = useState(false);
  const failingPath = location.pathname || "/";
  const redacted = redactRoute(failingPath);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-10">
      <PageHeader
        title="Page not found"
        subtitle="That link does not match anything here. It may have been renamed, mistyped, or never existed."
      />

      <p className="text-sm text-muted">
        Looking for{" "}
        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-ink">
          {redacted}
        </code>
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white no-underline hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        >
          Go home
        </Link>
        <Link
          to="/community"
          className="inline-flex items-center justify-center rounded-lg border border-line bg-white px-4 py-2.5 text-sm font-semibold text-ink no-underline hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20"
        >
          Browse community
        </Link>
        <Button type="button" variant="ghost" onClick={() => setReportOpen(true)}>
          Report this broken link
        </Button>
      </div>

      <ReportProblemDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        initialType="bug"
        surfaceId="page.404"
        route={failingPath}
        prefillBody={`I landed on a missing page. The URL I expected was: ${redacted}`}
        payload={{ failingRoute: redacted }}
      />
    </div>
  );
}
